<template>
<div class="menu-list manager">
    <card v-if="isLoading">
        <template slot="header">
            <i class="material-icons">store_mall_directory</i>
            <strong class="ml-2">Your facility</strong>
        </template>

        <template slot="body">
            <div class="flex ai-c">
                <img src="@/assets/img/loading-black-32.gif" alt="loading" />
                <span class="ml-1">Loading your facility information...</span>
            </div>
        </template>
    </card>

    <div v-else-if="!facility">
        <card>
            <template slot="header">
                <span class="warning-text"><i class="material-icons">warning</i></span>
                <strong class="ml-2">Warning</strong>
            </template>

            <template slot="body">
                <span>You are not set up as a facility owner in our system! If you think this is an error, please reach out to Kim O&rsquo;Neill at <a href="mailto:kim.oneill@storable.com">kim.oneill@storable.com</a>.</span>
            </template>
        </card>
    </div>

    <div v-else>
        <div class="flex jc-sb">
            <div class="flex-40 mr-2">
                <card>
                    <template slot="header">
                        <div>
                            <i class="material-icons">store_mall_directory</i>
                            <strong class="ml-2">Your facility</strong>
                        </div>
                    </template>

                    <template slot="body">
                        <div class="flex">
                            <span>You are managing facility:</span>
                            <strong class="ml-2">
                                <div>{{ facility.account_name }}</div>
                                <div>{{ facility.address }}</div>
                                <div>{{ facility.city }}, {{ facility.state }} {{ facility.zip_code }}</div>
                                <div class="mt-2">{{ facility_phone }}</div>
                                <div>{{ facility.email }}</div>
                            </strong>
                        </div>

                        <div class="mt-2">If any of the information above is incorrect, please contact Kim at (844) 410-2733 or <a href="mailto:kim.oneill@storable.com">kim.oneill@storable.com</a>.</div>

                        <hr />

                        <div>You have a total of <strong>{{ facility.stats.total_all }}</strong> tenants in the system.</div>

                        <div class="mt-2">You will have until 2 days before the Audit Letter date to select any tenants who have moved out.</div>

                        <div v-show="facility.cycles.length > 1" class="mt-5 ta-r">
                            <router-link class="button" to="/master-audit">Master Audit</router-link>
                        </div>

                        <!-- <div v-if="audit_letters_sent_today" class="success-message mt-5">
                            <i class="material-icons">markunread_mailbox</i>

                            <span class="ml-2">Audit letters were mailed out today!</span>
                        </div>

                        <div v-if="reminder_letters_sent_today" class="success-message mt-5">
                            <i class="material-icons">markunread_mailbox</i>

                            <span class="ml-2">Reminder letters were mailed out today!</span>
                        </div> -->
                    </template>
                </card>
            </div>

            <div class="flex-60 ml-2">
                <card v-for="(cycle, index) in facility.cycles" :key="cycle.id" size="small" :class="['cycle-card', { 'mt-4': (index > 0) }]">
                    <template slot="body">
                        <div><strong>{{ (cycle.label) ? cycle.label : "Unnamed" }}</strong></div>

                        <div class="mt-3">
                            <strong>{{ cycle.stats.total || 0 }}</strong> tenants are in this cycle.
                        </div>

                        <div v-if="cycle.can_tag_removal && cycle.ready_to_tag" class="flag-message mt-5">
                            <i class="material-icons">warning</i>

                            <div class="ml-2">
                                <span v-if="!cycle || !cycle.initial_letter_send_date || !cycle.move_out_date">You have until 2 days before the Audit Letter Date to select any tenants who have moved out.</span>
                                <span v-else>You have until {{ dayjs(cycle.move_out_date).format('MM/DD/YYYY') }} to select any tenants who have moved out.</span>
                            </div>
                        </div>

                        <div class="flex ai-fe jb-sb mt-3 mb-3 fs-09">
                            <div class="flex-33 mr-2">
                                <label>Audit Letter Date</label>
                                <input disabled type="text" :value="printDate(cycle.initial_letter_send_date)" />
                            </div>

                            <div class="flex-33 ml-2 mr-2">
                                <label>Reminder Letter Date</label>
                                <input disabled type="text" :value="printDate(cycle.reminder_letter_send_date)" />
                            </div>

                            <div class="flex-33 ml-2">
                                <label>Letter Effective Date</label>
                                <input disabled type="text" :value="printDate(cycle.letter_effective_date)" />
                            </div>
                        </div>

                        <div class="flex ai-fe jb-sb mt-3 mb-3 fs-09">
                            <div class="flex-33 mr-2">
                                <label>Update Responses Deadline Date</label>
                                <input v-if="cycle.update_responses_deadline_date === null" disabled type="text" value="--" />
                                <input v-else disabled type="text" :value="printDate(cycle.update_responses_deadline_date)" />
                            </div>

                            <div class="flex-33 ml-2 mr-2">
                                <label>Setup Coverage Dates</label>
                                <div class="date-range flex ai-c">
                                    <div>
                                        <input v-if="cycle.setup_coverage_start_date === null" disabled type="text" value="--" />
                                        <input v-else disabled type="text" :value="printDate(cycle.setup_coverage_start_date)" />
                                    </div>

                                    <span class="fs-09 mt-1 mb-1 ml-2 mr-2">to</span>

                                    <div>
                                        <input v-if="cycle.setup_coverage_end_date === null" disabled type="text" value="--" />
                                        <input v-else disabled type="text" :value="printDate(cycle.setup_coverage_end_date)" />
                                    </div>
                                </div>
                            </div>

                            <div class="flex-33 ml-2">
                                <label>Monitor for Payment Dates</label>
                                <div class="date-range flex ai-c">
                                    <div>
                                        <input v-if="cycle.monitor_payment_start_date === null" disabled type="text" value="--" />
                                        <input v-else disabled type="text" :value="printDate(cycle.monitor_payment_start_date)" />
                                    </div>

                                    <span class="fs-09 mt-1 mb-1 ml-2 mr-2">to</span>

                                    <div>
                                        <input v-if="cycle.monitor_payment_end_date === null" disabled type="text" value="--" />
                                        <input v-else disabled type="text" :value="printDate(cycle.monitor_payment_end_date)" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <hr class="small" />

                        <div v-if="cycle.stats">
                            <div class="flex fd-c">
                                <div class="flex ai-fe jc-sb mt-1">
                                    <span>Bader Coverage</span>
                                    <div class="ml-5">
                                        <span>{{ (cycle.stats.selected_coverage) ? cycle.stats.selected_coverage : 0 }}</span>
                                        <span class="ml-1 badge">{{ cycle.stats.selected_coverage_percentage }}%</span>
                                    </div>
                                </div>

                                <div class="flex ai-fe jc-sb">
                                    <span style="text-indent: 15px;">&mdash;&nbsp;Charge Applied</span>
                                    <div class="ml-5">
                                        <span>{{ (cycle.stats.charge_applied) ? cycle.stats.charge_applied : 0 }}</span>
                                        <span class="ml-1 badge">{{ cycle.stats.charge_applied_percentage }}%</span>
                                    </div>
                                </div>

                                <div class="flex ai-fe jc-sb mt-1">
                                    <span>Opted Out</span>
                                    <div class="ml-5">
                                        <span>{{ (cycle.stats.opted_out) ? cycle.stats.opted_out : 0 }}</span>
                                        <span class="ml-1 badge">{{ cycle.stats.opted_out_percentage }}%</span>
                                    </div>
                                </div>

                                <div class="flex ai-fe jc-sb mt-1">
                                    <span>Provided Proof</span>
                                    <div class="ml-5">
                                        <span>{{ (cycle.stats.provided_proof) ? cycle.stats.provided_proof : 0 }}</span>
                                        <span class="ml-1 badge">{{ cycle.stats.provided_proof_percentage }}%</span>
                                    </div>
                                </div>

                                <div class="flex ai-fe jc-sb mt-1">
                                    <span>Potential for Coverage</span>
                                    <div class="ml-5">
                                        <span>{{ cycle.stats.non_responder }}</span>
                                        <span class="ml-1 badge">{{ cycle.stats.non_responder_percentage }}%</span>
                                    </div>
                                </div>
                            </div>

                            <hr class="small" />

                            <div class="flex ai-fe jc-sb">
                                <span>Total Active Tenants</span>
                                <strong class="ml-5 total-count">{{ cycle.stats.total }}</strong>
                            </div>

                            <div class="flex ai-c mt-3 fs-09 gray-message">
                                <i class="material-icons">announcement</i>
                                <span class="ml-1">Ignores tenants with a <em>bad address</em> or have <em>moved out</em>.</span>
                            </div>
                        </div>

                        <div class="mt-3">
                            <router-link class="button medium" :to="`/cycle-audit/${cycle.id}`">Enter Audit</router-link>
                        </div>
                    </template>
                </card>
            </div>
        </div>
    </div>
</div>
</template>

<script>
import Vue from "vue"
import store from "@/store"
import dayjs from "dayjs"
import ManagerSetupTenantsModal from "@/components/home-page/ManagerSetupTenantsModal.vue"

export default {
    data () {
        return {
            dayjs: dayjs,
            isLoading: true
        }
    },

    computed: {
        audit_letters_sent_today () {
            if (!this.facility || !this.facility.initial_letter_send_date) {
                return false
            }

            const letterSendDate = this.facility.initial_letter_send_date
            const today = new Date()
            const hour = today.getHours()

            // we want to simulate "attentiveness", so always return false before noon
            if (hour < 12) {
                return false
            }

            if (letterSendDate instanceof Date) {
                return letterSendDate.toDateString() === today.toDateString()
            }

            let year = today.getFullYear()
            let month = today.getMonth() + 1
            let day = today.getDate()

            if (`${month}`.length < 2) {
                month = `0${month}`
            }

            if (`${day}`.length < 2) {
                day = `0${day}`
            }

            return letterSendDate === `${year}-${month}-${day}`
        },

        reminder_letters_sent_today () {
            if (!this.facility || !this.facility.reminder_letter_send_date) {
                return false
            }

            const letterSendDate = this.facility.reminder_letter_send_date
            const today = new Date()
            const hour = today.getHours()

            // we want to simulate "attentiveness", so always return false before noon
            if (hour < 12) {
                return false
            }

            if (letterSendDate instanceof Date) {
                return letterSendDate.toDateString() === today.toDateString()
            }

            let year = today.getFullYear()
            let month = today.getMonth() + 1
            let day = today.getDate()

            if (`${month}`.length < 2) {
                month = `0${month}`
            }

            if (`${day}`.length < 2) {
                day = `0${day}`
            }

            return letterSendDate === `${year}-${month}-${day}`
        },

        facility () {
            return store.state.userFacility.facility
        },

        facility_phone () {
            if (!this.facility || !this.facility.phone) {
                return ""
            }

            const raw = this.facility.phone.replace(/[^0-9]/g, "")

            if (raw.length === 10) {
                return `(${raw.slice(0, 3)}) ${raw.slice(3, 6)}-${raw.slice(6)}`
            }

            return raw
        },

        move_out_date () {
            if (!this.facility || !this.facility.initial_letter_send_date) {
                return null
            }

            // get the date and normalize the hours to prevent time zone jumping
            const date = new Date(this.facility.initial_letter_send_date)
            date.setUTCHours(date.getHours())

            // set the day to two days earlier
            date.setDate(date.getDate() - 2)

            // left pad the day value with a 0, if necessary
            let day = date.getDate()
            if (String(day).length < 2) {
                day = `0${day}`
            }

            return `${date.getMonth() + 1}/${day}/${date.getFullYear()}`
        }
    },

    mounted () {
        store.dispatch("userFacility/loadFacility").then(response => {
            this.isLoading = false
            this.normalizeDates()

            this.checkLetterEffectiveDateStatus(response.data.cycles)
        })
    },

    methods: {
        /**
         * Check all cycles and display a warning modal if any have tenants
         * that need to be setup within 5 days of the letter effective date.
         */
        checkLetterEffectiveDateStatus (cycles) {
            const threshold = dayjs().subtract(5, "day")

            const warningCycles = cycles.filter((cycle) => {
                const letterEffectiveDate = dayjs(cycle.letter_effective_date)

                return letterEffectiveDate.isSameOrAfter(threshold)
            })

            if (warningCycles.length !== 0) {
                const componentConstructor = Vue.extend(ManagerSetupTenantsModal)

                this.$eventManager.emit("show-modal", {
                    component: componentConstructor,
                    props: {
                        cycles: warningCycles
                    }
                })
            }
        },

        /**
         * Normalize the letter send dates because JavaScript automatically subtracts hours
         * for local time, making it appear as if the date selected is the day before.
         */
        normalizeDates () {
            if (this.facility.initial_letter_send_date) {
                const date = new Date(this.facility.initial_letter_send_date)
                date.setUTCHours(date.getHours())
                this.facility.initial_letter_send_date = date
            }

            if (this.facility.reminder_letter_send_date) {
                const date = new Date(this.facility.reminder_letter_send_date)
                date.setUTCHours(date.getHours())
                this.facility.reminder_letter_send_date = date
            }
        },

        printDate (rawDate) {
            const date = new Date(`${rawDate} 00:00:00`)

            return date.toLocaleDateString()
        }
    }
}
</script>

<style scoped>
.badge {
    text-align: center;
    width: 65px;
}

.total-count {
    padding-right: 74px;
}

.flag-message {
    align-items: center;
    background: #fecbcb;
    border-radius: 2px;
    color: #b41b1b;
    display: flex;
    padding: 4px 6px;
}

.success-message {
    align-items: center;
    background: #bbffdf;
    border-radius: 2px;
    color: #1ea44e;
    display: flex;
    padding: 4px 6px;
}

.cycle-card {
    position: relative;
}

.cycle-card-blocker {
    background: rgba(255, 255, 255, 0.75);
    display: none;
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
}

.cycle-card.inactive .cycle-card-blocker {
    align-items: center;
    content: "";
    display: flex;
    justify-content: center;
}

@media (max-width: 1230px) {
    .date-range {
        flex-direction: column;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/home-page/ManagerMenuList.vue